<template>
  <div class="h-full">
    <div v-if="!messageSent">
      <FormulateForm v-model="form" @submit="onSubmit">
        <div class="flex flex-col space-y-6">
          <h3>Send a message to {{ connection.name }}</h3>

          <FormulateInput
            name="name"
            label="Your name"
            validation="required"
            :disabled="example"
            :class="example ? 'cursor-not-allowed' : ''"
          />

          <FormulateInput
            name="email"
            label="Your email"
            validation="required|email"
            :disabled="example"
            :class="example ? 'cursor-not-allowed' : ''"
          />

          <FormulateInput
            v-if="tagsPreferences?.length"
            name="preferences"
            type="tags"
            label="Choose from preferences"
            validation-name="Preference selection"
            :display-only="true"
            :options="tagsPreferences"
            :count="form.preferences?.length || 0"
            :is-collapsed="isTagsCollapsed.preferences"
            :connection="connection"
            :class="example ? 'pointer-events-none' : ''"
          />

          <FormulateInput
            v-if="tagsSchoolSupport?.length"
            name="schoolSupport"
            type="tags"
            label="Choose from support types"
            validation-name="School support selection"
            :options="tagsSchoolSupport"
            :count="form.schoolSupport?.length || 0"
            :is-collapsed="isTagsCollapsed.schoolSupport"
            :class="example ? 'pointer-events-none' : ''"
          />

          <FormulateInput
            v-if="tagsOrganisations?.length"
            name="organisationsSupport"
            type="tags"
            label="Choose from career support types"
            validation-name="Career support area selection"
            :options="tagsOrganisations"
            :count="form.organisationsSupport?.length || 0"
            :is-collapsed="isTagsCollapsed.organisationsSupport"
            :class="example ? 'pointer-events-none' : ''"
          />

          <FormulateInput
            name="message"
            type="textarea"
            label="Message"
            validation="required"
            :disabled="example"
            :class="example ? 'cursor-not-allowed' : ''"
          />
          <div class="relative">
            <FormulateInput
              v-show="!loading"
              type="submit"
              label="Send"
              :disabled="example"
              :class="example ? 'pointer-events-none' : ''"
            />
            <button
              v-show="loading"
              class="btn bg-black absolute left-0 top-0 h-14"
            >
              <lottie :options="loadingAnimationOptions" class="h-8 w-8" />
            </button>
          </div>
        </div>
      </FormulateForm>
    </div>
    <div v-else class="flex flex-col items-center justify-center h-full">
      <img src="/img/connections/icons/success.png" alt="Checkmark" />
      <h3 class="text-center m-4">Success!</h3>
      <p class="lg:w-1/2 text-center mb-6">
        Your message has been sent to {{ connection.name }}
      </p>

      <p class="text-center text-truegray-500">
        Check your emails for a reply soon!
      </p>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useStore } from '../../../../../js/store/index.js'
import { useRoute } from 'vue-router/composables'
import * as loadingAnimation from '../../../../../shared/components/loading.json'
import { EventBus } from '../../../../../js/plugins/event-bus.js'
import { EVENT_KEYS } from '../../../../../js/util/constants.js'

export default defineComponent({
  name: 'ModalProfileMessage',

  props: {
    connection: {
      type: Object,
      required: true,
    },

    example: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const loadingAnimationOptions = { animationData: loadingAnimation.default }
    const store = useStore()
    const route = useRoute()
    const isContributor = computed(() => store.getters['shared/isContributor'])
    const isPublicProfileContext = computed(() =>
      store.getters['contributors/isPublicProfileContext'](route),
    )
    const form = ref({
      email: '',
    })
    const loading = ref(false)
    const isTagsCollapsed = ref({
      preferences: false,
      schoolSupport: false,
      organisationsSupport: false,
    })

    const onSubmit = async () => {
      loading.value = true
      const action = isPublicProfileContext.value
        ? 'contributors/dispatchSendProfileConnectionMessage'
        : isContributor.value
        ? 'contributors/dispatchSendMessage'
        : 'connections/dispatchSendMessage'
      await store.dispatch(action, {
        connectionId: props.connection.id,
        payload: form.value,
      })
      resetForm()
      messageSent.value = true
      loading.value = false
    }

    const resetForm = () => {
      form.value = {}
    }

    const messageSent = ref(false)

    const tagsPreferences = computed(() => {
      return store.getters['shared/getTagsMappedForVueFormulate'](
        props.connection.tags_preferences,
        'getTagsPreferences',
      )
    })

    const tagsSchoolSupport = computed(() =>
      store.getters['shared/getTagsMappedForVueFormulate'](
        props.connection.tags_school_support,
        'getTagsSchoolSupport',
      ),
    )

    const tagsOrganisations = computed(() =>
      store.getters['shared/getTagsMappedForVueFormulate'](
        props.connection.tags_organisations,
        'getTagsOrganisations',
      ),
    )

    onMounted(() => {
      const user = store.getters['shared/getUser']
      if (user) {
        form.value.email = user.email
      }

      EventBus.$on(
        EVENT_KEYS.CO_VUE_FORMULATE_TAGS_TOGGLE_COLLAPSED,
        (type) => {
          const value = isTagsCollapsed.value[type]
          isTagsCollapsed.value[type] = !value
        },
      )
    })

    return {
      form,
      loading,
      loadingAnimationOptions,
      onSubmit,
      messageSent,
      resetForm,
      tagsPreferences,
      tagsSchoolSupport,
      tagsOrganisations,
      isTagsCollapsed,
    }
  },
})
</script>
